import type { AnalyticsMessages } from '../../models/i18n/analytics.messages'
import type {
  KpisConfig,
  KpisConfigProvider,
} from '../../providers/interfaces/kpis-config.provider'
import { getCurrentInstance } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum, KpiEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { mapGetters } from '../utils'

export interface KpisOptionComposable {
  id: KpiEnum
  label: string
}

export interface KpisComposable {
  supportedKpis: KpiEnum[]
  findabilityEngagementKpi: KpiEnum
  findKpiOptionById: (id: KpiEnum) => KpisOptionComposable | undefined
  kpis: KpiEnum[]
  kpiFields: Record<string, string> | undefined
  kpiOptions: Array<KpisOptionComposable>
  KpisOfTools: KpisConfig['tools']
  getKpiStyle: (kpi: KpiEnum) => KpiStyle
  getKpiLabel: (kpi: KpiEnum) => string
  getKpiValue: ({
    kpi,
    data,
    usePercentage,
  }: {
    kpi: KpiEnum
    data: Record<string, any>
    usePercentage?: boolean
  }) => number
}

interface KpiStyle {
  color: ColorEnum
  bgColor?: ColorEnum
  icon?: string
}

export const useKpis = (): KpisComposable | undefined => {
  const currentInstance = getCurrentInstance()

  if (!currentInstance) {
    return
  }

  const messages: AnalyticsMessages = currentInstance.proxy.$epb.messages.insights.analytics

  const { kpis: instanceKpis } = mapGetters<KpiEnum[]>(
    currentInstance.proxy.$store,
    CONFIG_CONSTS.moduleName,
    {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    },
  )

  const getKpisConfigProvider = CONTAINER.get<KpisConfigProvider>(TYPES.KpisConfigProvider)
  const { findabilityEngagementKpi, kpiFields, orderOfKpis, tools } = getKpisConfigProvider()

  const kpis = instanceKpis.value
    .filter((kpi: KpiEnum) => Object.values(KpiEnum).includes(kpi))
    .sort((kpi, prevKpi) => {
      const prevKpiIndex = orderOfKpis.indexOf(prevKpi)

      if (prevKpiIndex === -1) {
        return -1
      }

      return orderOfKpis.indexOf(kpi) - prevKpiIndex
    })

  const supportedKpis = kpis.filter((kpi: KpiEnum) => orderOfKpis.includes(kpi))

  const kpiMessages: AnalyticsMessages['kpis'] = {
    ...messages.kpis,
    [KpiEnum.NO_RESULTS]: messages.kpis.noResults,
  }

  const kpiOptions = supportedKpis.map(kpi => ({
    id: kpi,
    label: capitalizeSentence(kpiMessages[kpi]?.valueLabel ?? kpi),
  }))

  const findKpiOptionById = (id: KpiEnum): KpisOptionComposable | undefined => {
    return kpiOptions.find(option => option.id === id)
  }

  const getKpiStyle = (kpi: KpiEnum): KpiStyle => {
    const kpiStyleMap: Partial<Record<KpiEnum, KpiStyle>> = {
      [KpiEnum.CLICKS]: {
        color: ColorEnum.GREEN_75,
        bgColor: ColorEnum.GREEN_5,
        icon: 'actions',
      },
      [KpiEnum.ADD2CART]: {
        color: ColorEnum.OCEAN_75,
        bgColor: ColorEnum.BLUE_5,
        icon: 'add2cart',
      },
      [KpiEnum.QUERIES]: {
        icon: 'search',
        color: ColorEnum.YELLOW_75,
        bgColor: ColorEnum.YELLOW_5,
      },
      [KpiEnum.NO_RESULTS]: {
        icon: 'noresults',
        color: ColorEnum.RED_75,
        bgColor: ColorEnum.RED_5,
      },
      [KpiEnum.POSITION_AVG]: {
        color: ColorEnum.YELLOW_75,
        bgColor: ColorEnum.ORANGE_5,
        icon: 'hash',
      },
      [KpiEnum.CONVERSION]: {
        icon: 'findability',
        color: ColorEnum.GREEN_100,
        bgColor: ColorEnum.GREEN_25,
      },
      [KpiEnum.CHECKOUT]: {
        icon: 'checkouts',
        color: ColorEnum.OCEAN_75,
        bgColor: ColorEnum.OCEAN_5,
      },
    }

    return kpiStyleMap[kpi] ?? { color: ColorEnum.PRIMARY }
  }

  const getKpiLabel = (kpi: KpiEnum): string => {
    const label =
      kpi === KpiEnum.CONVERSION
        ? kpiMessages[KpiEnum.FINDABILITY].percentageLabel
        : kpiMessages[kpi]?.valueLabel

    return label ?? '-'
  }

  const getKpiValue = ({
    kpi,
    data,
    usePercentage = false,
  }: {
    kpi: KpiEnum
    data: Record<string, any>
    usePercentage?: boolean
  }): number => {
    const kpisConfig: Record<string, number> = {
      [KpiEnum.QUERIES]: usePercentage ? data?.queryCountPct : data?.queryCount,
      [KpiEnum.CLICKS]: usePercentage ? data?.clickCountPct : data?.clickCount,
      [KpiEnum.FINDABILITY]: data?.findability,
      [KpiEnum.ADD2CART]: usePercentage ? data?.add2CartPct : data?.add2cartCount,
      [KpiEnum.NO_RESULTS]: usePercentage ? data?.noResultsCountPct : data?.noResultsCount,
      [KpiEnum.CONVERSION]: data?.successCountPct,
      [KpiEnum.CHECKOUT]: usePercentage ? data?.checkoutCountPct : data?.checkoutCount,
    }
    return kpisConfig[kpi] ?? 0
  }

  return {
    supportedKpis,
    findabilityEngagementKpi,
    findKpiOptionById,
    kpis,
    kpiFields,
    kpiOptions,
    KpisOfTools: tools,
    getKpiStyle,
    getKpiLabel,
    getKpiValue,
  }
}
